import React from "react";
import "react-datepicker/dist/react-datepicker.module.css";
import Select, { Creatable } from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faIndent, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Button } from "aible";
import { ConditionType, ConditionUtils } from "types/condition";
import { column } from "types/expression";
import { customMapper, mapToSelectOption, mapToSelectOptions, selectOnChangeWrapper, selectOnChangeWrapperMulti } from "utils/select";
import * as RelationUtils from "utils/relation";
import styles from "./style.module.css";
import { ColumnOrScalarInput, ColumnValueInput, DateValueInput, ToggleableColumnOrScalarInput, MappedCategoricalValueInput } from "components/Tiles/Sense/Relation/Common";
const ContainmentValueInput = ({ condition, field, onChange, isDisabled }) => {
    const values = (condition.values ?? []).map((x) => String(x));
    let options = [];
    let handleChange = (x) => onChange({
        type: condition.type,
        column: condition.column,
        values: x
    });
    if (field.type == "TEXT") {
        options = (field.values ?? []).map(String).filter((value) => value !== "{Other}" && value !== "{Missing}");
    }
    if (field.type == "NUMERIC") {
        handleChange = (x) => {
            const numeric = x.map(Number).filter((x) => !isNaN(x));
            onChange({
                type: condition.type,
                column: condition.column,
                values: numeric
            });
        };
    }
    return (React.createElement("div", { style: { marginLeft: "-10px" } },
        React.createElement(Creatable, { value: mapToSelectOption(values), onChange: selectOnChangeWrapperMulti(handleChange), options: mapToSelectOptions(options), className: styles.containmentValueInput, placeholder: options.length > 0 ? "Select Value" : "", menuPlacement: "bottom", label: "Value(s)", isMulti: true, isDisabled: isDisabled || !condition.column, isClearable: false, noOptionsMessage: (_) => "Type in a value" })));
};
const ComparisonValueInput = ({ condition, field, context, onChange, isDisabled }) => {
    return (React.createElement(ToggleableColumnOrScalarInput, { value: condition.value, onChange: (x) => onChange({ type: condition.type, column: condition.column, value: x }), isDisabled: isDisabled || ConditionUtils.isInvalidColumn(condition.column), context: context, output: field, isCreateable: true }));
};
const IntervalValueInput = ({ condition, field, context, onChange, isDisabled }) => {
    const handleMinChange = (left) => {
        if (left === undefined) {
            return;
        }
        else if (condition.right !== undefined &&
            (ConditionUtils.isString(left) || ConditionUtils.isNumber(left)) &&
            left >= condition.right) {
            onChange({ ...condition, left: left, right: left });
        }
        else {
            onChange({ ...condition, left: left });
        }
    };
    const handleMaxChange = (right) => {
        if (right === undefined) {
            return;
        }
        else if (condition.left !== undefined &&
            (ConditionUtils.isString(right) || ConditionUtils.isNumber(right)) &&
            right <= condition.left) {
            onChange({ ...condition, left: right, right: right });
        }
        else {
            onChange({ ...condition, right: right });
        }
    };
    if (field.type == "DATE" &&
        (condition.left === undefined || ConditionUtils.isString(condition.left)) &&
        (condition.right === undefined || ConditionUtils.isString(condition.right))) {
        return (React.createElement("div", null,
            React.createElement(DateValueInput, { value: condition.left, onChange: handleMinChange, isDisabled: isDisabled || ConditionUtils.isInvalidColumn(condition.column), start: condition.left, end: condition.right, placeholder: "Select Start Date", isStart: true }),
            React.createElement("div", { style: { marginTop: "10px" } }),
            React.createElement(DateValueInput, { value: condition.right, onChange: handleMaxChange, isDisabled: isDisabled || ConditionUtils.isInvalidColumn(condition.column), start: condition.left, end: condition.right, placeholder: "Select End Date", isEnd: true })));
    }
    return (React.createElement("div", null,
        React.createElement(ColumnOrScalarInput, { value: condition.left, onChange: handleMinChange, isDisabled: isDisabled || ConditionUtils.isInvalidColumn(condition.column), context: context, output: field, isCreateable: true }),
        React.createElement("div", { style: { marginTop: "10px" } }),
        React.createElement(ColumnOrScalarInput, { value: condition.right, onChange: handleMaxChange, isDisabled: isDisabled || ConditionUtils.isInvalidColumn(condition.column), context: context, output: field, isCreateable: true })));
};
const ColumnConditionValueInput = ({ condition, context, onChange, isDisabled }) => {
    const field = condition.column !== undefined
        ? (context?.[condition.column] ?? {
            type: "TEXT",
            data: []
        })
        : undefined;
    if (field === undefined) {
        return React.createElement(React.Fragment, null);
    }
    if (ConditionUtils.isInterval(condition)) {
        return (React.createElement(IntervalValueInput, { condition: condition, field: field, onChange: onChange, context: context, isDisabled: isDisabled }));
    }
    else if (ConditionUtils.isContainment(condition)) {
        return (React.createElement(ContainmentValueInput, { condition: condition, field: field, onChange: onChange, context: context, isDisabled: isDisabled }));
    }
    else if (ConditionUtils.isComparison(condition)) {
        return (React.createElement(ComparisonValueInput, { condition: condition, field: field, onChange: onChange, context: context, isDisabled: isDisabled }));
    }
    else if (ConditionUtils.isNull(condition)) {
        return React.createElement(React.Fragment, null);
    }
    else {
        return React.createElement(React.Fragment, null);
    }
};
const NUMERIC_TYPES = [
    ConditionType.LessThan,
    ConditionType.LessThanOrEqual,
    ConditionType.GreaterThan,
    ConditionType.GreaterThanOrEqual,
    ConditionType.Equal,
    ConditionType.NotEqual,
    ConditionType.Between,
    ConditionType.NotBetween,
    ConditionType.Null,
    ConditionType.NotNull
];
const DATE_TYPES = [
    ConditionType.LessThan,
    ConditionType.LessThanOrEqual,
    ConditionType.GreaterThan,
    ConditionType.GreaterThanOrEqual,
    ConditionType.Equal,
    ConditionType.NotEqual,
    ConditionType.Between,
    ConditionType.NotBetween,
    ConditionType.Null,
    ConditionType.NotNull
];
const TEXT_TYPES = [
    ConditionType.Equal,
    ConditionType.NotEqual,
    ConditionType.In,
    ConditionType.NotIn,
    ConditionType.Null,
    ConditionType.NotNull
];
const getTypesForField = (field) => {
    if (field === undefined) {
        return TEXT_TYPES;
    }
    else if (field.type == "NUMERIC") {
        return NUMERIC_TYPES;
    }
    else if (field.type == "DATE") {
        return DATE_TYPES;
    }
    else {
        return TEXT_TYPES;
    }
};
const COLUMN_CONDITION_TYPE_MAPPER = customMapper((type) => ConditionUtils.getDisplayNameForType(type));
const ColumnConditionTypeInput = ({ condition, context, onChange, isDisabled }) => {
    const field = ConditionUtils.isInvalidColumn(condition.column) ? undefined : context[condition.column];
    const validTypes = getTypesForField(field);
    const handleChange = (type) => {
        if (type == condition.type) {
            return; // no update needed
        }
        else if (ConditionUtils.isComparison(condition) && ConditionUtils.isComparisonType(type)) {
            onChange({
                type: type,
                column: condition.column,
                value: condition.value
            });
        }
        else if (ConditionUtils.isContainment(condition) && ConditionUtils.isContainmentType(type)) {
            onChange({
                type: type,
                column: condition.column,
                values: condition.values
            });
        }
        else if (ConditionUtils.isInterval(condition) && ConditionUtils.isIntervalType(type)) {
            onChange({
                type: type,
                column: condition.column,
                left: condition.left,
                right: condition.right,
                closed: condition.closed
            });
        }
        else if (ConditionUtils.isNull(condition) && ConditionUtils.isNullType(type)) {
            onChange({
                type: type,
                column: condition.column
            });
        }
        else {
            const fillValue = ((o) => {
                switch (o?.type) {
                    case "BOOLEAN":
                        return true;
                    case "NUMERIC":
                        return 0;
                    case "TEXT":
                        return "";
                    case "DATE":
                    case "NULL":
                    case undefined:
                    default:
                        return undefined;
                }
            })(context[condition.column]);
            onChange(ConditionUtils.getDefaultColumnCondition(type, condition.column, fillValue));
        }
    };
    return (React.createElement(MappedCategoricalValueInput, { value: condition.type, mapper: COLUMN_CONDITION_TYPE_MAPPER, values: validTypes, onChange: (type) => handleChange(type ?? ConditionType.Equal), isDisabled: isDisabled || ConditionUtils.isInvalidColumn(condition.column), selectOnly: true }));
};
const CollectionConditionTypeInput = ({ condition, onChange, isDisabled }) => {
    const validTypes = [ConditionType.And, ConditionType.Or];
    const value = ConditionUtils.getDisplayNameForType(condition.type);
    const values = validTypes.map((type) => ConditionUtils.getDisplayNameForType(type));
    const nameToType = (x) => {
        switch (x) {
            case "Or":
                return ConditionType.Or;
            case "And":
            default:
                return ConditionType.And;
        }
    };
    return (React.createElement("div", { className: styles.columnInput, style: { width: "80px" } },
        React.createElement(Select, { value: mapToSelectOption(value), onChange: selectOnChangeWrapper((x) => onChange({
                type: nameToType(x),
                conditions: condition.conditions
            })), options: mapToSelectOptions(values), isDisabled: isDisabled })));
};
const ColumnConditionInput = ({ condition, context, onChange, isDisabled }) => {
    const handleColumnChange = (column) => {
        if (column !== undefined && column != condition.column && context[column] && context[column].type) {
            const validTypes = getTypesForField(context[column]);
            if (validTypes.indexOf(condition.type) >= 0) {
                const update = ConditionUtils.getDefaultColumnCondition(condition.type, column);
                onChange(update);
                return;
            }
        }
        onChange({
            type: ConditionType.Equal,
            column: "",
            value: undefined
        });
    };
    return (React.createElement("div", { className: styles.conditionHolder },
        React.createElement("div", { className: styles.conditionHolderItem },
            React.createElement(ColumnValueInput, { value: column(condition.column), context: context, onChange: (c) => handleColumnChange(c?.column), isDisabled: isDisabled })),
        React.createElement("div", { className: styles.conditionHolderItem },
            React.createElement(ColumnConditionTypeInput, { condition: condition, context: context, onChange: onChange, isDisabled: isDisabled })),
        React.createElement("div", { className: styles.conditionHolderItem },
            React.createElement(ColumnConditionValueInput, { condition: condition, context: context, onChange: onChange, isDisabled: isDisabled }))));
};
export const BaseConditionInput = ({ condition, onChange, context, isDisabled }) => {
    if (ConditionUtils.isColumn(condition)) {
        return React.createElement(ColumnConditionInput, { condition: condition, onChange: onChange, context: context, isDisabled: isDisabled });
    }
    else if (ConditionUtils.isCollection(condition)) {
        return (React.createElement(CollectionConditionInput, { condition: condition, onChange: onChange, context: context, isDisabled: isDisabled }));
    }
    else if (ConditionUtils.isNot(condition)) {
        return React.createElement(NotConditionInput, { condition: condition, onChange: onChange, context: context, isDisabled: isDisabled });
    }
    else {
        return React.createElement(React.Fragment, null);
    }
};
export const ConditionInput = ({ condition, onChange, context, isDisabled }) => {
    const c = ConditionUtils.isCollection(condition)
        ? condition
        : {
            type: ConditionType.And,
            conditions: [condition]
        };
    return (React.createElement(BaseConditionInput, { condition: c, onChange: (update) => {
            if (ConditionUtils.isCollection(update) && update.conditions.length == 1) {
                onChange(update.conditions[0]);
            }
            else {
                onChange(update);
            }
        }, context: context, isDisabled: isDisabled }));
};
const NotConditionInput = ({ condition, onChange, context, isDisabled }) => {
    const handleChange = (childUpdate) => {
        onChange({
            type: ConditionType.Not,
            condition: childUpdate
        });
    };
    return (React.createElement("div", { className: styles.conditionListContents },
        React.createElement(BaseConditionInput, { condition: condition.condition, onChange: handleChange, context: context, isDisabled: isDisabled }),
        React.createElement("div", { className: styles.conditionListModeToggle, style: { paddingLeft: "10px", width: "90px" } }, "Is Not True")));
};
class CollectionConditionInput extends React.PureComponent {
    static defaultProps = {
        depth: 0,
        maxDepth: 1,
        onEmpty: (_) => {
            return;
        },
        defaultCondition: {
            type: ConditionType.Equal,
            column: undefined,
            value: undefined
        }
    };
    handleUpdate(conditions) {
        const updatedConditions = conditions.filter((c) => c !== undefined);
        this.props.onChange({
            type: this.props.condition.type,
            conditions: updatedConditions
        });
    }
    handleChange(index, childUpdate) {
        const update = this.props.condition.conditions.slice();
        update[index] = childUpdate;
        this.handleUpdate(update);
    }
    addCondition() {
        const update = this.props.condition.conditions.slice();
        update.push(this.props.defaultCondition);
        this.handleUpdate(update);
    }
    removeCondition(removeIndex) {
        const { condition, onEmpty } = this.props;
        const update = condition.conditions.filter((_, index) => index != removeIndex);
        this.handleUpdate(update);
        if (update.length === 0 && condition.conditions[removeIndex] !== undefined) {
            onEmpty(condition.conditions[removeIndex]);
        }
    }
    convertToCollection(index) {
        const update = this.props.condition.conditions.slice();
        const collection = {
            type: ConditionType.And,
            conditions: [update[index]]
        };
        update[index] = collection;
        this.handleUpdate(update);
    }
    renderCondition(condition, index) {
        const { context, depth, maxDepth, isDisabled } = this.props;
        const onChange = (update) => this.handleChange(index, update);
        return (React.createElement("div", { key: index, className: styles.conditionListItemHolder },
            React.createElement("div", { className: styles.removeCondition },
                React.createElement(Button, { type: "secondary", onClick: () => this.removeCondition(index), style: { padding: "5px" }, title: "Remove condition", disabled: isDisabled },
                    React.createElement(FontAwesomeIcon, { icon: faTrash }))),
            !ConditionUtils.isCollection(condition) && (React.createElement("div", { className: styles.removeCondition },
                React.createElement(Button, { type: "secondary", onClick: () => this.convertToCollection(index), disabled: isDisabled || depth >= maxDepth, style: { padding: "5px" }, title: "Add sub-criteria" },
                    React.createElement(FontAwesomeIcon, { icon: faIndent })))),
            ConditionUtils.isCollection(condition) ? (React.createElement(CollectionConditionInput, { condition: condition, onChange: onChange, context: context, depth: depth + 1, onEmpty: (last) => this.handleChange(index, last), isDisabled: isDisabled })) : (React.createElement(BaseConditionInput, { condition: condition, onChange: (update) => this.handleChange(index, update), context: context, isDisabled: isDisabled }))));
    }
    render() {
        const { condition, onChange, context, isDisabled } = this.props;
        return (React.createElement("div", { className: styles.conditionListHolder },
            React.createElement("div", { className: styles.conditionListContents },
                React.createElement("div", null, condition.conditions.map((c, index) => this.renderCondition(c, index))),
                condition.conditions.length > 1 && (React.createElement("div", { className: styles.conditionListModeToggle },
                    React.createElement(CollectionConditionTypeInput, { condition: condition, context: context, onChange: onChange, isDisabled: isDisabled })))),
            React.createElement("div", { className: styles.addCondition },
                React.createElement(Button, { type: "secondary", onClick: () => this.addCondition(), disabled: isDisabled },
                    React.createElement(FontAwesomeIcon, { icon: faPlus, style: { marginRight: "5px" } }),
                    "Add Criteria"))));
    }
}
class ConditionListInput extends React.PureComponent {
    handleUpdate(condition) {
        if (ConditionUtils.isCollection(condition) && condition.type == ConditionType.And) {
            this.props.onChange(condition.conditions);
        }
        else {
            this.props.onChange([condition]);
        }
    }
    render() {
        const { conditions, dataset, isDisabled } = this.props;
        const relation = RelationUtils.fromDataset(dataset);
        const context = RelationUtils.getOutputContext(relation);
        const condition = conditions.length == 1 && conditions[0].type == ConditionType.Or
            ? conditions[0]
            : {
                type: ConditionType.And,
                conditions: conditions
            };
        return (React.createElement(BaseConditionInput, { context: context, condition: condition, onChange: (condition) => this.handleUpdate(condition), isDisabled: isDisabled }));
    }
}
export default ConditionListInput;
